import { FC, RefObject, useEffect, useRef, useState } from 'react'

import { Dialog } from './Dialog'

type ComponentProps = {
  [key: string]: unknown
}

type DialogProps = {
  onClose: () => void
}

/**
 * A Higher order component that wraps the Dialog. By using it, your state will be clean every time
 * the dialog opens.
 */
export const withDialog = <C extends ComponentProps>(
  Component: FC<
    DialogProps &
      C & {
        isOpen: boolean
        initialFocus: RefObject<any>
      }
  >,
) => {
  return function WithDialogComponent(
    props: DialogProps &
      Omit<C, 'openData' | 'isOpen'> &
      (C extends {
        openData: unknown
      }
        ? {
            openData: C['openData'] | undefined
          }
        : {
            isOpen: boolean
          }),
  ) {
    // Getting "could be instantiated with a different subtype of constraint" error otherwise
    const TSHappyComponent = Component as FC<
      ComponentProps & DialogProps & { isOpen: boolean }
    >

    const isOpen = !!(props.isOpen || props.openData)
    const [dialogIsOpen, setDialogIsOpen] = useState(isOpen)
    const [internalOpenData, setInternalOpenData] = useState(props.openData)
    const initialFocus = useRef(null)

    useEffect(() => {
      // So that dialogIsOpen and componentIsOpen are set in the same render cycle
      setDialogIsOpen(isOpen)

      if (isOpen) {
        // Need to keep old value when dialog is fading out
        setInternalOpenData(props.openData)
      }
    }, [isOpen, props.dialogIsOpen])

    return (
      <Dialog
        isOpen={dialogIsOpen}
        onClose={props.onClose}
        initialFocus={initialFocus}
      >
        <TSHappyComponent
          {...props}
          isOpen={isOpen}
          openData={internalOpenData}
          initialFocus={initialFocus}
        />
      </Dialog>
    )
  }
}
